import React, { useImperativeHandle, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { EllipsisHorizontalIcon, PlusIcon } from '@heroicons/react/20/solid';
import Drawer from '@ui/drawer/Drawer';
import useDrawer from '@ui/drawer/DrawerRef';
import Dialogue from '@app/component/overlays/dialog/Dialogue';
import useDialogue from '@app/component/overlays/dialog/DialogueRef';
import FormButton from '@app/component/form/button/FormButton';
import FormInputText from '@app/component/form/input/text/FormInputText';
import GroupBoard, { GroupBoardGroup } from '@app/component/board/GroupBoard';
import Message from '@app/component/message/Message';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import { SegmentGroups } from '@app/model/bi/Bi';
import { useSaveSegmentGroups } from '@app/model/bi/service/BiHooks';
import { keyOf, segmentGroupsChanged } from './SegmentGroups.utilities';

export type SegmentGroupsDrawerHandle = { open: () => void };

type Props = {
	data: SegmentGroups,
	companyName: string,
	// All properties of the company, also the properties that the user cannot see: the groups belong to the company.
	propertyCount: number,
};

const NOT_GROUPED = 'not-grouped';

// A group in the editor: key = the board id (the saved id as text, or "new-…"); id = the saved id, null for a new group.
type DraftGroup = { key: string, id: number | null, title: string, segments: string[] };
const number = new Intl.NumberFormat('en-GB');

// The group editor (grill decisions 22–25): the user drags each PMS segment of the company into one of the company's own
// groups. The groups and the assignments belong to the company: one assignment of a segment name covers every property
// that uses that name. The board keeps the changes until Save groups. With changes, each close action asks first
// (ROO2-244); Save groups and "Close without saving" close with the ref, which never asks.
const SegmentGroupsDrawer = React.forwardRef<SegmentGroupsDrawerHandle, Props>(({ data, companyName, propertyCount }, ref) => {
	const drawer = useDrawer();
	const nameDialogue = useDialogue<string | null>();
	const deleteDialogue = useDialogue<string>();
	const closeDialogue = useDialogue<void>();
	const toaster = useToaster();
	const [groups, setGroups] = useState<DraftGroup[]>([]);
	const [editing, setEditing] = useState<string | null>(null);
	const nameForm = useForm<{ title: string }>({ defaultValues: { title: '' } });
	const save = useSaveSegmentGroups(
		() => {
			toaster.success('Groups', 'RoomPulse saved the groups. The reports of all properties use them now.');
			drawer.close();
		},
		error => toaster.error('Groups', error),
	);

	useImperativeHandle(ref, () => ({
		// The editor always starts from the saved groups: a company with no group starts empty (no suggestions, ROO2-244).
		open: () => {
			setGroups(data.groups.map(g => ({ key: String(g.id), id: g.id, title: g.title, segments: [...g.segments] })));
			drawer.open();
		},
	}));

	const changed = segmentGroupsChanged(groups, data.groups);
	// The X, Close, the overlay and Escape: with changes, ask first.
	const requestClose = () => changed ? closeDialogue.open() : drawer.close();

	const nightsOf = useMemo(() => new Map(data.segments.map(s => [keyOf(s.name), s])), [data.segments]);
	const placed = new Set(groups.flatMap(g => g.segments.map(keyOf)));
	const loose = data.segments.filter(s => !placed.has(keyOf(s.name))).map(s => s.name);
	const totalOf = (names: string[]) => names.reduce((sum, n) => sum + (nightsOf.get(keyOf(n))?.roomNights ?? 0), 0);
	const item = (name: string) => {
		const s = nightsOf.get(keyOf(name));
		return { id: name, label: name, meta: s === undefined ? undefined : `${s.properties} ${s.properties === 1 ? 'property' : 'properties'} · ${number.format(s.roomNights)} RN` };
	};

	const actions = (group: DraftGroup) => <Menu as="div" className="relative">
		<MenuButton aria-label={`Actions of the group ${group.title}`} className="rounded p-0.5 text-gray-400 hover:text-gray-600">
			<EllipsisHorizontalIcon aria-hidden="true" className="h-5 w-5" />
		</MenuButton>
		<MenuItems anchor="bottom end" className="z-30 w-36 rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
			<MenuItem>
				<span className="block cursor-pointer px-3 py-1.5 text-gray-700 hover:bg-gray-50"
					onClick={() => { setEditing(group.key); nameForm.reset({ title: group.title }); nameDialogue.open(group.key); }}>Rename</span>
			</MenuItem>
			<MenuItem>
				<span className="block cursor-pointer px-3 py-1.5 text-red-700 hover:bg-red-50"
					onClick={() => { setEditing(group.key); deleteDialogue.open(group.key); }}>Delete</span>
			</MenuItem>
		</MenuItems>
	</Menu>;

	const board: GroupBoardGroup[] = [
		{
			id: NOT_GROUPED, title: 'Not grouped', tone: 'attention', items: loose.map(item),
			subtitle: loose.length === 0 ? 'Every segment has a group' : `${number.format(totalOf(loose))} RN in the imported stays`,
		},
		...groups.map(g => ({
			id: g.key, title: g.title, items: g.segments.map(item), actions: actions(g),
			subtitle: `${number.format(totalOf(g.segments))} RN in the imported stays`,
		})),
	];

	const move = (segment: string, to: string) => setGroups(current => current.map(g => ({
		...g,
		segments: g.key === to ? [...g.segments.filter(s => s !== segment), segment] : g.segments.filter(s => s !== segment),
	})));

	const confirmName = nameForm.handleSubmit(({ title }) => {
		const clean = title.trim();
		if (clean.length === 0) return;
		setGroups(current => editing === null
			? [...current, { key: `new-${Date.now()}`, id: null, title: clean, segments: [] }]
			: current.map(g => g.key === editing ? { ...g, title: clean } : g));
		nameDialogue.close();
	});

	const titles = groups.map(g => g.title.trim().toLowerCase());
	const duplicate = titles.some((t, i) => titles.indexOf(t) !== i);
	const editingTitle = groups.find(g => g.key === editing)?.title ?? '';

	return <Drawer title="Groups of PMS segments" size="huge" ref={drawer.ref} onCloseRequest={requestClose}
		actions={<FormButton state="primary" isLoading={save.isPending} disabled={duplicate}
			onClick={() => save.mutate(groups.map(g => ({ id: g.id, title: g.title, segments: g.segments })))}>
			Save groups
		</FormButton>}>
		<div className="flex flex-col gap-4 px-4 py-6 sm:px-6">
			<div className="flex flex-wrap items-start justify-between gap-3">
				<p className="text-sm text-gray-500">
					Drag each PMS segment into a group. {data.segments.length} segments, {groups.length} groups.
				</p>
				<FormButton state="white" size="small" icon={<PlusIcon aria-hidden="true" className="h-4 w-4" />}
					onClick={() => { setEditing(null); nameForm.reset({ title: '' }); nameDialogue.open(null); }}>
					New group
				</FormButton>
			</div>
			<Message state="info" flush>
				The groups apply to every property of {companyName} ({propertyCount} properties). A segment name that several properties use
				has one group. Nights with no PMS segment stay in “No segment”.
			</Message>
			{duplicate && <Message state="error" flush>Two groups have the same name. Rename one of them.</Message>}
			{changed && (data.groups.length === 0
				? <Message state="warning" title="Groups not saved" flush>The report stays by PMS segments until you click Save groups.</Message>
				: <Message state="warning" title="Changes not saved" flush>The reports use the saved groups until you click Save groups.</Message>)}
			<GroupBoard groups={board} ariaLabel="Groups of PMS segments" emptyLabel="Drag segments here"
				onMove={move} />
		</div>
		<Dialogue ref={nameDialogue.ref} title={editing === null ? 'New group' : 'Rename the group'} size="small"
			confirmButtonName={editing === null ? 'Add group' : 'Rename'} cancelButtonName="Cancel" isAutoClose={false}
			onConfirm={() => void confirmName()}>
			<FormInputText form={nameForm} name="title" label="Name" placeholder="For example OTAs" maxLength={40} isRequired />
		</Dialogue>
		<Dialogue ref={deleteDialogue.ref} title={`Delete the group “${editingTitle}”?`} size="small"
			confirmButtonName="Delete" cancelButtonName="Cancel"
			onConfirm={() => setGroups(current => current.filter(g => g.key !== editing))}>
			<p className="text-sm text-gray-500">Its segments go to “Not grouped”. Nothing changes before Save groups.</p>
		</Dialogue>
		<Dialogue ref={closeDialogue.ref} title="Close without saving?" size="small"
			confirmButtonName="Close without saving" cancelButtonName="Keep editing" onConfirm={() => drawer.close()}>
			<p className="text-sm text-gray-500">RoomPulse drops the changes to the groups. The reports keep the saved groups.</p>
		</Dialogue>
	</Drawer>;
});

export default SegmentGroupsDrawer;
